import supabase from '../supabase/supabase';

const TABLE = 'articles_1774300800';
const SOURCES_TABLE = 'rss_sources_1774300800';
const fallbackImage = 'https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1200&q=80';

const formatDate = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(value));
};

const formatTime = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en', {
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
};

const estimateReadTime = (article) => {
  const words = `${article.excerpt || ''} ${article.content || ''}`
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .length;

  return `${Math.max(1, Math.ceil(words / 220))} min read`;
};

const formatViews = (value) => {
  const views = Number(value || 0);
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
  if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
  return String(views);
};

export const mapArticle = (article) => ({
  id: article.id,
  slug: article.slug,
  category: article.category,
  label: article.article_type === 'rss'
    ? 'RSS / SOURCE'
    : article.article_type.toUpperCase(),
  title: article.title,
  excerpt: article.excerpt,
  content: article.content || '',
  image: article.image || fallbackImage,
  author: article.author || `${article.source} News Desk`,
  source: article.source,
  sourceUrl: article.source_url,
  date: formatDate(article.published_at),
  time: formatTime(article.published_at),
  readTime: estimateReadTime(article),
  views: formatViews(article.view_count),
  viewCount: Number(article.view_count || 0),
  featured: article.featured,
  publishedAt: article.published_at,
  contentPermission: article.content_permission
});

export const fetchArticles = async ({
  category,
  limit = 100,
  orderBy = 'published_at'
} = {}) => {
  let query = supabase
    .from(TABLE)
    .select('*')
    .eq('status', 'published')
    .lte('published_at', new Date().toISOString())
    .order(orderBy, { ascending: false })
    .limit(limit);

  if (category && category !== 'News' && category !== 'Trending') {
    query = query.eq('category', category);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapArticle);
};

export const fetchArticleBySlug = async (slug) => {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();

  if (error) throw error;
  return data ? mapArticle(data) : null;
};

export const fetchRssSources = async () => {
  const { data, error } = await supabase
    .from(SOURCES_TABLE)
    .select('*')
    .order('category')
    .order('name');

  if (error) throw error;

  return (data || []).map((source) => ({
    ...source,
    updated: source.last_successful_update
      ? `${formatDate(source.last_successful_update)} at ${formatTime(source.last_successful_update)}`
      : 'Never imported',
    imported: source.articles_imported
  }));
};

export const runRssIngestion = async () => {
  const { data, error } = await supabase.functions.invoke('rss-ingest', {
    body: { source: 'admin-manual-refresh' }
  });

  if (error) {
    throw new Error(
      error.message || 'The RSS ingestion function could not be reached.'
    );
  }

  if (!data?.success) {
    throw new Error(data?.error || 'RSS ingestion did not complete.');
  }

  return data;
};